/**
 * XRDepthSystem — Accesses depth sensing data each frame.
 *
 * Phase: RenderSub, SubPhase: PreUpdate, Priority: 10
 * Active only during AR sessions with 'depth-sensing' feature granted.
 */

import type { ISystem } from '../../ecs/System.js';
import type { World } from '../../ecs/World.js';
import type { EngineRenderer } from '../../render/Renderer.js';
import type { XRSessionState } from '../types.js';
import { LoopPhase, SubPhase } from '../../shared/types.js';
import { Logger } from '../../core/Logger.js';

export class XRDepthSystem implements ISystem {
  readonly name = 'XRDepth';
  readonly phase = LoopPhase.RenderSub;
  readonly subPhase = SubPhase.PreUpdate;
  readonly priority = 10;

  private _state: XRSessionState;
  private _warnedMissingFrame = false;

  constructor(state: XRSessionState, _renderer: EngineRenderer) {
    this._state = state;
  }

  execute(_dt: number, _world: World): void {
    if (!this._state.isPresenting) return;
    if (!this._state.hasFeature('depth-sensing')) return;

    // Reset depth info each frame
    this._state.cpuDepthInfo = null;

    const frame = (globalThis as any).__xrFrame as XRFrame | undefined;
    if (!frame) {
      if (!this._warnedMissingFrame) {
        Logger.warn('XRDepth', '__xrFrame missing during active session — App animation loop not populating globalThis.__xrFrame');
        this._warnedMissingFrame = true;
      }
      return;
    }

    const refSpace = this._state.referenceSpace;
    if (!refSpace) return;

    // Get viewer pose for depth info access
    try {
      const viewerPose = frame.getViewerPose(refSpace);
      if (!viewerPose || viewerPose.views.length === 0) return;

      // Get depth for the first view (typically left eye or mono)
      const view = viewerPose.views[0];
      const depthInfo = frame.getDepthInformation(view);
      if (depthInfo) {
        this._state.cpuDepthInfo = depthInfo as XRCPUDepthInformation;
      }
    } catch (err) {
      // Depth sensing may not be available for this frame. Debug level so
      // transient drops during tracking loss don't spam a released build,
      // but failures still surface on a headset devtools connection.
      Logger.debug('XRDepth', `getDepthInformation failed: ${err}`);
    }
  }
}
